import { apiAxios } from '@/api'
import { keyBy } from 'lodash'

import {
  type ActivateAs,
  FinancialAccount,
  FinancialAccountPrimaryUse,
  receiveCreateFinancialAccount,
  receiveDeactivateFinancialAccount,
  receiveDeleteFinancialAccount,
  receiveFinancialAccounts,
  receiveReactivateFinancialAccount,
  receiveUpdateFinancialAccount,
  TransactionSyncState,
} from '../reducers/finances/financialAccountsReducer'
import { fetchIfNeededWrapper, fetchWrapper } from '../reducers/fetch'
import { PlaidAccount } from 'react-plaid-link'
import { fetchPlaidItemsIfNeeded } from './plaidItemActions'
import {
  receiveUpdatePlaidItem,
  PlaidItem,
} from '../reducers/finances/plaidItemReducer'

interface FinancialAccountUpdateRequest {
  markedPersonalAt?: Date | null
  isHidden?: boolean
  transactionSyncState?: TransactionSyncState
  onboardingAnswers?: {
    [key: string]: boolean | undefined
  }
  hasPersonalTransactions?: boolean
  hasBusinessTransactions?: boolean
  primaryUse?: FinancialAccountPrimaryUse
}

export const FETCH_FINANCIAL_ACCOUNT_KEY = 'FETCH_FINANCIAL_ACCOUNT_KEY'
export const fetchFinancialAccountsIfNeeded = (
  alwaysFetch = false,
  { unreconciledCounts = false }: { unreconciledCounts?: boolean } = {}
) =>
  fetchIfNeededWrapper({
    defaultErrorMessage: 'There was an error fetching your connected accounts.',
    alwaysFetch,
    fetchKey: FETCH_FINANCIAL_ACCOUNT_KEY,
    fetchFunction: (dispatch) =>
      apiAxios
        .get<
          FinancialAccount[]
        >(unreconciledCounts ? '/finances/api/v1/accounts?unreconciledCounts=true' : '/finances/api/v1/accounts')
        .then((json) => {
          dispatch(receiveFinancialAccounts(keyBy(json.data, 'id')))
          return json.data
        }),
  })

export const updateFinancialAccount = (
  financialAccountId: number,
  data: FinancialAccountUpdateRequest
) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error updating your financial account',
    fetchFunction: (dispatch) =>
      apiAxios
        .put<FinancialAccount>(
          `/finances/api/v1/accounts/${financialAccountId}`,
          data
        )
        .then((json) => {
          dispatch(receiveUpdateFinancialAccount(json.data))
          return json.data
        }),
  })

export const DEACTIVATE_FINANCIAL_ACCOUNT_KEY =
  'DEACTIVATE_FINANCIAL_ACCOUNT_KEY'
export const deactivateFinancialAccount = (financialAccountId: number) =>
  fetchWrapper({
    fetchKey: DEACTIVATE_FINANCIAL_ACCOUNT_KEY,
    defaultErrorMessage:
      'There was an error deactivating the financial account.',
    defaultValue: false,
    fetchFunction: (dispatch) =>
      apiAxios
        .post<{
          id: number
        }>(`/finances/api/v1/accounts/${financialAccountId}/deactivate`)
        .then(() => {
          dispatch(receiveDeactivateFinancialAccount(financialAccountId))
          return true
        }),
  })

export const REACTIVATE_FINANCIAL_ACCOUNT_KEY =
  'REACTIVATE_FINANCIAL_ACCOUNT_KEY'
export const reactivateFinancialAccount = (
  financialAccountId: number,
  activateAs: ActivateAs
) =>
  fetchWrapper({
    fetchKey: REACTIVATE_FINANCIAL_ACCOUNT_KEY,
    defaultErrorMessage:
      'There was an error reactivating the financial account.',
    fetchFunction: async (dispatch) => {
      const response = await apiAxios.post<{ id: number } | { code: string }>(
        `/finances/api/v1/accounts/${financialAccountId}/reactivate`,
        { activateAs },
        { validateStatus: (status) => status === 200 || status === 409 }
      )
      if (response.status === 409) {
        await dispatch(fetchFinancialAccountsIfNeeded(true))
        return { needsPlaidLink: true as const }
      }
      dispatch(
        receiveReactivateFinancialAccount({ financialAccountId, activateAs })
      )
      return { needsPlaidLink: false as const }
    },
  })

export const deleteFinancialAccount = (financialAccountId: number) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error removing the financial account.',
    fetchFunction: (dispatch) =>
      apiAxios
        .delete<{
          deletedId: number
          financialProfileId: number
        }>(`/finances/api/v1/accounts/${financialAccountId}`)
        .then((json) => {
          dispatch(receiveDeleteFinancialAccount(json.data.deletedId))
          return json.data
        }),
  })

export const UPDATE_PLAID_ITEM_KEY = 'UPDATE_PLAID_ITEM_KEY'
// Updating Plaid
export const updatePlaidItem = (
  plaidItemId: number | null,
  data: {
    markedCannotReconnectAt?: Date | null
    needsReconnection?: boolean | null
    connectedOn?: Date
    disconnectedOn?: null
    errorObject?: null
    automaticStatements?: boolean
  }
) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error updating the PlaidItem.',
    fetchKey: UPDATE_PLAID_ITEM_KEY,
    fetchFunction: (dispatch) => {
      if (!plaidItemId) {
        return null
      }
      return apiAxios
        .post<PlaidItem>(`/finances/api/v1/plaid/items/${plaidItemId}`, data)
        .then((json) => {
          // Refetch financial accounts after update
          dispatch(fetchFinancialAccountsIfNeeded(true))
          dispatch(receiveUpdatePlaidItem(json.data))
          return json.data
        })
    },
  })

// deletes a user's plaid item, only used during onboarding
export const deletePlaidItem = (plaidItemId: number) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error deleting the Plaid Item.',
    fetchFunction: (dispatch) =>
      apiAxios
        .delete<number>(`/finances/api/v1/plaid/items/${plaidItemId}`)
        .then((json) => {
          dispatch(fetchFinancialAccountsIfNeeded(true))
          dispatch(fetchPlaidItemsIfNeeded(true))
          return json.data
        }),
  })

// Send Plaid link event data to our db
export const createPlaidLinkEvent = (
  data:
    | {
        institutionId?: string | null
        linkSessionId: string
        errorCode: string
        errorType: string | null
        exitStatus: string | null
        eventType: string
      }
    | {
        institutionId?: string
        accounts: PlaidAccount[]
        linkSessionId: string
        eventType: string
      }
) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error creating a plaid link event.',
    fetchFunction: () => {
      return apiAxios.post<Record<string, unknown>>(
        '/finances/api/v1/plaid/link_event',
        data
      )
    },
  })

// Updating Plaid. Pass `additive: true` when the account list isn't authoritative (e.g. reauth flows) —
// missing accounts won't be inactivated.
export const updateAccounts = (
  plaidItemId: number | null,
  data: { plaidAccounts: PlaidAccount[]; additive?: boolean }
) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error updating the Plaid accounts.',
    fetchFunction: (dispatch) => {
      if (!plaidItemId) {
        return null
      }
      return apiAxios
        .post<PlaidItem>(`/finances/api/v1/accounts/${plaidItemId}`, data)
        .then((json) => {
          // Refetch financial accounts after update
          dispatch(fetchFinancialAccountsIfNeeded(true))
          dispatch(receiveUpdatePlaidItem(json.data))
          return json.data
        })
    },
  })

export interface CreateManualAccountRequest {
  plaidInstitutionName: string
  name: string
  mask: string
  type: string
  subtype: string
}

export const CREATE_MANUAL_ACCOUNT_KEY = 'CREATE_MANUAL_ACCOUNT_KEY'
export const createManualAccount = (data: CreateManualAccountRequest) =>
  fetchWrapper({
    fetchKey: CREATE_MANUAL_ACCOUNT_KEY,
    defaultErrorMessage: 'There was an error creating the manual account.',
    fetchFunction: (dispatch) =>
      apiAxios
        .post<FinancialAccount>('/finances/api/v1/accounts/manual', data)
        .then((json) => {
          dispatch(receiveCreateFinancialAccount(json.data))
          return json.data
        }),
  })

export interface AddUnconnectedAccountRequest {
  institutionName: string
  mask: string
  accountKind: 'checking' | 'savings' | 'credit_card'
}

export interface AddUnconnectedAccountResponse {
  outcome: 'created' | 'reused'
  financialAccountId: number
  accountLabel: string
}

export const ADD_UNCONNECTED_ACCOUNT_KEY = 'ADD_UNCONNECTED_ACCOUNT_KEY'
export const addUnconnectedAccount = (data: AddUnconnectedAccountRequest) =>
  fetchWrapper({
    fetchKey: ADD_UNCONNECTED_ACCOUNT_KEY,
    defaultErrorMessage: 'There was an error adding the account.',
    fetchFunction: (dispatch) =>
      apiAxios
        .post<AddUnconnectedAccountResponse>(
          '/finances/api/v1/accounts/manual/unconnected',
          data
        )
        .then((json) => {
          dispatch(fetchFinancialAccountsIfNeeded(true))
          return json.data
        }),
  })

export enum StatementStatusType {
  PLAID_STATEMENT = 'plaid_statement',
  LIMITED_BANK_ACCESS = 'limited_bank_access',
  NO_SUPPORT = 'no_support',
}
export interface StatementStatus {
  id: number
  status: StatementStatusType
}

export const ACCOUNT_UPLOAD_STATUSES_KEY = 'ACCOUNT_UPLOAD_STATUSES_KEY'
export const getAccountUploadStatuses = () =>
  fetchWrapper({
    fetchKey: ACCOUNT_UPLOAD_STATUSES_KEY,
    defaultErrorMessage: 'There was an error getting upload statuses.',
    fetchFunction: () =>
      apiAxios
        .get<
          StatementStatus[]
        >('/finances/api/v1/accounts/statement_upload_statuses')
        .then((json) => {
          return json.data
        }),
  })
